import { useMemo, useState } from 'react';
import { TrendingUp, Users, Wallet, Sparkles, AlertTriangle } from 'lucide-react';
import { useSalaryAnalysis, type SalaryAnalysisRow } from '../../../hooks';
import { thb, inputCls, btnGhost, EmptyState, TableShell, SectionHeader } from './_shared';

/**
 * วิเคราะห์เงินเดือน (Salary Analysis) — OWNER + HR เต็มสิทธิ์ (edit_salary).
 * ส่วนบน = ภาพรวม/การกระจายค่าจ้าง + compa-ratio. ส่วนล่าง = ตัวจำลองปรับขึ้น
 * (คิดฝั่ง client จาก rows ที่ backend ส่งมา — ไม่บันทึกอะไร).
 */

type RaiseMode = 'percent' | 'flat';
type Scope = 'all' | 'below' | `dept:${string}`;
type Zone = 'below' | 'within' | 'above' | null;

const ZONE_LABEL: Record<'below' | 'within' | 'above' | 'none', string> = {
  below: 'ต่ำกว่ากรอบ', within: 'อยู่ในกรอบ', above: 'สูงกว่ากรอบ', none: 'ไม่มีกรอบ',
};
const ZONE_CLR: Record<'below' | 'within' | 'above' | 'none', string> = {
  below: 'text-amber-700 bg-amber-50',
  within: 'text-emerald-700 bg-emerald-50',
  above: 'text-sky-700 bg-sky-50',
  none: 'text-gray-500 bg-gray-100',
};

function zoneOf(base: number, band: SalaryAnalysisRow['band']): Zone {
  if (!band) return null;
  return base < band.min ? 'below' : base > band.max ? 'above' : 'within';
}
const zoneKey = (z: Zone) => z ?? 'none';

function Kpi({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-[var(--border)] bg-white p-4">
      <div className="flex items-center gap-2 text-[var(--text-muted)]"><span className="text-[var(--brand-600)]">{icon}</span><span className="text-xs">{label}</span></div>
      <div className="mt-1 text-xl font-bold text-[var(--text)]">{value}</div>
      {sub ? <div className="text-xs text-[var(--text-muted)]">{sub}</div> : null}
    </div>
  );
}

function ZonePill({ z }: { z: Zone }) {
  const k = zoneKey(z);
  return <span className={`rounded-full px-2 py-0.5 text-xs ${ZONE_CLR[k]}`}>{ZONE_LABEL[k]}</span>;
}

export function SalaryAnalysisTab() {
  const q = useSalaryAnalysis();
  const [mode, setMode] = useState<RaiseMode>('percent');
  const [amount, setAmount] = useState<string>('5');
  const [scope, setScope] = useState<Scope>('all');

  const data = q.data;
  const rows = useMemo(() => data?.rows ?? [], [data]);

  // ── simulation (pure, client-side) ──
  const sim = useMemo(() => {
    const amt = Number(amount) || 0;
    const inScope = (r: SalaryAnalysisRow) => {
      if (scope === 'all') return true;
      if (scope === 'below') return zoneOf(r.base_salary, r.band) === 'below';
      return r.department === scope.slice('dept:'.length);
    };
    const proposedOf = (r: SalaryAnalysisRow) =>
      inScope(r) ? (mode === 'percent' ? Math.round(r.base_salary * (1 + amt / 100)) : r.base_salary + amt) : r.base_salary;

    let curTotal = 0, newTotal = 0, affected = 0;
    const movers = { toWithin: 0, toAbove: 0, stillBelow: 0 };
    const impact: Array<{ r: SalaryAnalysisRow; proposed: number; delta: number; zBefore: Zone; zAfter: Zone }> = [];
    for (const r of rows) {
      const proposed = proposedOf(r);
      curTotal += r.base_salary;
      newTotal += proposed;
      if (proposed !== r.base_salary) {
        affected++;
        const zBefore = zoneOf(r.base_salary, r.band);
        const zAfter = zoneOf(proposed, r.band);
        if (zBefore === 'below' && zAfter === 'within') movers.toWithin++;
        if (zAfter === 'above') movers.toAbove++;
        if (zBefore === 'below' && zAfter === 'below') movers.stillBelow++;
        impact.push({ r, proposed, delta: proposed - r.base_salary, zBefore, zAfter });
      }
    }
    impact.sort((a, b) => b.delta - a.delta);
    const deltaMonth = newTotal - curTotal;
    return { curTotal, newTotal, deltaMonth, deltaYear: deltaMonth * 12, affected, movers, impact };
  }, [rows, mode, amount, scope]);

  if (q.isLoading) return <p className="py-10 text-center text-sm text-[var(--text-muted)]">กำลังโหลด…</p>;
  if (q.isError) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-800">
        <AlertTriangle className="mx-auto mb-2 h-6 w-6" />
        ฟีเจอร์นี้สำหรับ<b>เจ้าของ</b>หรือ<b>HR สิทธิ์เต็ม</b> (แก้ไขฐานเงินเดือน) เท่านั้น
      </div>
    );
  }
  if (!data || rows.length === 0) {
    return <EmptyState text="ยังไม่มีพนักงานที่ตั้งฐานเงินเดือน — ตั้งฐานเงินเดือนก่อนจึงจะวิเคราะห์ได้" />;
  }

  const s = data.summary;
  const deptOptions = data.by_department.map((d) => d.department);

  return (
    <div className="space-y-8">
      {/* ── ภาพรวม ── */}
      <section className="space-y-3">
        <SectionHeader title="ภาพรวมค่าจ้าง" subtitle="ฐานเงินเดือนปัจจุบันของพนักงานที่คิดเงินเดือน (ไม่รวมเจ้าของที่ยกเว้น)" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi icon={<Users className="h-4 w-4" />} label="พนักงาน" value={`${s.headcount} คน`} />
          <Kpi icon={<Wallet className="h-4 w-4" />} label="ค่าจ้างรวม/เดือน" value={thb(s.total_base)} sub={`≈ ${thb(s.total_base * 12)}/ปี`} />
          <Kpi icon={<TrendingUp className="h-4 w-4" />} label="เฉลี่ย / มัธยฐาน" value={thb(s.avg)} sub={`มัธยฐาน ${thb(s.median)}`} />
          <Kpi icon={<TrendingUp className="h-4 w-4" />} label="ต่ำสุด–สูงสุด" value={`${thb(s.min)}`} sub={`สูงสุด ${thb(s.max)}`} />
        </div>
      </section>

      {/* ── compa-ratio zones ── */}
      <section className="space-y-3">
        <SectionHeader title="เทียบกับกรอบเงินเดือน (compa-ratio)" subtitle="คนที่อยู่นอกกรอบ = จุดที่ควรทบทวน (ต่ำกว่า = เสี่ยงลาออก, สูงกว่า = ต้นทุนเกินกรอบ)" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {([['below', s.compa.below], ['within', s.compa.within], ['above', s.compa.above], ['none', s.compa.no_band]] as const).map(([z, n]) => (
            <div key={z} className={`rounded-xl p-4 ${ZONE_CLR[z]}`}>
              <div className="text-xs">{ZONE_LABEL[z]}</div>
              <div className="mt-1 text-2xl font-bold">{n}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── by department ── */}
      <section className="space-y-3">
        <SectionHeader title="แยกตามแผนก" />
        <TableShell head={
          <tr>
            <th className="px-3 py-2 font-medium">แผนก</th>
            <th className="px-3 py-2 text-right font-medium">คน</th>
            <th className="px-3 py-2 text-right font-medium">เฉลี่ย</th>
            <th className="px-3 py-2 text-right font-medium">ต่ำสุด–สูงสุด</th>
            <th className="px-3 py-2 text-right font-medium">รวม/เดือน</th>
          </tr>
        }>
          {data.by_department.map((d) => (
            <tr key={d.department} className="border-t border-[var(--border)]">
              <td className="px-3 py-2 font-medium text-[var(--text)]">{d.department}</td>
              <td className="px-3 py-2 text-right">{d.count}</td>
              <td className="px-3 py-2 text-right">{thb(d.avg)}</td>
              <td className="px-3 py-2 text-right text-[var(--text-muted)]">{thb(d.min)}–{thb(d.max)}</td>
              <td className="px-3 py-2 text-right font-medium">{thb(d.total)}</td>
            </tr>
          ))}
        </TableShell>
      </section>

      {/* ── ตัวจำลองปรับขึ้น ── */}
      <section className="space-y-3">
        <SectionHeader title="จำลองการปรับขึ้นค่าจ้าง" subtitle="ลองปรับดูว่าต้นทุนจะเปลี่ยนเท่าไร — เป็นการคำนวณคร่าวๆ ไม่บันทึกลงระบบ" />
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-2)] p-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium">รูปแบบ</label>
              <div className="flex rounded-lg border border-[var(--border)] bg-white p-0.5 text-sm">
                <button onClick={() => setMode('percent')} className={`flex-1 rounded-md px-2 py-1.5 ${mode === 'percent' ? 'bg-[var(--brand-600)] text-white' : 'text-[var(--text-muted)]'}`}>เป็น %</button>
                <button onClick={() => setMode('flat')} className={`flex-1 rounded-md px-2 py-1.5 ${mode === 'flat' ? 'bg-[var(--brand-600)] text-white' : 'text-[var(--text-muted)]'}`}>เป็นบาท</button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{mode === 'percent' ? 'ปรับขึ้น (%)' : 'ปรับขึ้น (บาท/คน/เดือน)'}</label>
              <input className={inputCls} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">ใช้กับ</label>
              <select className={inputCls} value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
                <option value="all">ทุกคน</option>
                <option value="below">เฉพาะคนต่ำกว่ากรอบ</option>
                {deptOptions.map((d) => <option key={d} value={`dept:${d}`}>แผนก {d}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi icon={<Users className="h-4 w-4" />} label="คนที่ได้ปรับ" value={`${sim.affected} คน`} />
            <Kpi icon={<Wallet className="h-4 w-4" />} label="ค่าจ้างรวมใหม่/เดือน" value={thb(sim.newTotal)} sub={`เดิม ${thb(sim.curTotal)}`} />
            <Kpi icon={<TrendingUp className="h-4 w-4" />} label="เพิ่มขึ้น/เดือน" value={thb(sim.deltaMonth)} sub={sim.curTotal ? `+${((sim.deltaMonth / sim.curTotal) * 100).toFixed(1)}%` : undefined} />
            <Kpi icon={<Sparkles className="h-4 w-4" />} label="เพิ่มขึ้น/ปี" value={thb(sim.deltaYear)} />
          </div>

          {(sim.movers.toWithin > 0 || sim.movers.toAbove > 0) && (
            <p className="mt-3 text-xs text-[var(--text-muted)]">
              {sim.movers.toWithin > 0 && <>เลื่อนเข้ากรอบ {sim.movers.toWithin} คน · </>}
              {sim.movers.toAbove > 0 && <>เกินกรอบบน {sim.movers.toAbove} คน · </>}
              {sim.movers.stillBelow > 0 && <>ยังต่ำกว่ากรอบ {sim.movers.stillBelow} คน</>}
            </p>
          )}
        </div>

        {sim.impact.length > 0 && (
          <details className="rounded-xl border border-[var(--border)] bg-white">
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium">ดูผลรายคน ({sim.impact.length})</summary>
            <div className="border-t border-[var(--border)]">
              <TableShell head={
                <tr>
                  <th className="px-3 py-2 font-medium">พนักงาน</th>
                  <th className="px-3 py-2 text-right font-medium">เดิม</th>
                  <th className="px-3 py-2 text-right font-medium">ใหม่</th>
                  <th className="px-3 py-2 text-right font-medium">เพิ่ม</th>
                  <th className="px-3 py-2 font-medium">กรอบ</th>
                </tr>
              }>
                {sim.impact.slice(0, 200).map(({ r, proposed, delta, zBefore, zAfter }) => (
                  <tr key={r.emp_code} className="border-t border-[var(--border)]">
                    <td className="px-3 py-2">
                      <div className="font-medium text-[var(--text)]">{r.name}</div>
                      <div className="text-xs text-[var(--text-muted)]">{r.department ?? '—'}{r.position ? ` · ${r.position}` : ''}</div>
                    </td>
                    <td className="px-3 py-2 text-right text-[var(--text-muted)]">{thb(r.base_salary)}</td>
                    <td className="px-3 py-2 text-right font-medium">{thb(proposed)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">+{thb(delta)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <ZonePill z={zBefore} />
                        {zBefore !== zAfter && <><span className="text-[var(--text-muted)]">→</span><ZonePill z={zAfter} /></>}
                      </div>
                    </td>
                  </tr>
                ))}
              </TableShell>
              {sim.impact.length > 200 && <p className="px-3 py-2 text-xs text-[var(--text-muted)]">แสดง 200 คนแรก (เรียงตามยอดเพิ่มมากสุด)</p>}
            </div>
          </details>
        )}
      </section>
    </div>
  );
}
